/**
 * Report-schedules resource — typed wrappers over
 * `/v1/twin/report-schedules[/{id}]`. Backs scheduled report delivery (B2.4,
 * link delivery): a schedule names a report + recurrence + recipients, and the
 * backend reportscheduler emails a deep link when due. `next_run_at` is
 * server-computed from the recurrence.
 */

import { api } from "@/lib/api-client";
import { qs } from "./qs";
import type { Paginated } from "./types";

export type ScheduleFrequency = "daily" | "weekly" | "monthly";

export interface ReportSchedule {
  id: string;
  org_id: string;
  report_id: string;
  frequency: ScheduleFrequency;
  /** UTC hour, 0-23. */
  hour: number;
  /** 0=Sun..6=Sat (weekly); null otherwise. */
  day_of_week: number | null;
  /** 1-28 (monthly); null otherwise. */
  day_of_month: number | null;
  recipients: string[];
  enabled: boolean;
  next_run_at: string;
  last_sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateScheduleBody {
  report_id: string;
  frequency: ScheduleFrequency;
  hour?: number;
  day_of_week?: number;
  day_of_month?: number;
  recipients?: string[];
  enabled?: boolean;
}

export type UpdateScheduleBody = Partial<Omit<CreateScheduleBody, "report_id">>;

export const reportSchedules = {
  /** List schedules for the active org, optionally filtered to one report. */
  list: (reportId?: string) =>
    api.get<Paginated<ReportSchedule>>(`/v1/twin/report-schedules${qs({ report_id: reportId })}`),
  create: (body: CreateScheduleBody) =>
    api.post<ReportSchedule>("/v1/twin/report-schedules", body),
  update: (id: string, body: UpdateScheduleBody) =>
    api.patch<ReportSchedule>(`/v1/twin/report-schedules/${id}`, body),
  remove: (id: string) => api.delete<void>(`/v1/twin/report-schedules/${id}`),
};
